import { getRoomPageSettings, getStaticRooms, roomsKeys } from '@common/features/rooms';
import {
  getAllSpaPackagesPrefetch,
  getAvailableSpaTreatmentsPrefetch,
  getSpaDaysPagePrefetch,
  getSpaTreatmentsPagePrefetch,
} from '@common/features/spa/hooks';
import { PagePropsWithSSR } from '@common/features/state';
import { Home } from '@routes/Home';
import { getFeaturedModuleVisibility } from '@routes/Home/useFeaturedModuleVisibility';
import * as entityActions from '@store/entity/actions';
import { QueryClient, dehydrate } from '@tanstack/react-query';
import { NextPage, NextPageContext } from 'next';
import NextSeo from 'next-seo';
import Head from 'next/head';
import { Property } from 'shared-types/Property';

type HomePageProps = PagePropsWithSSR<{
  property: Property;
  language: string;
  url: string;
}>;

const HomePage: NextPage<HomePageProps> = ({ property, language, url }) => {
  const pageTitle = property.propertyTitle
    ? `${property.name} | ${property.propertyTitle}`
    : property.name;

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
      </Head>
      <Home id={property.id} language={language} />
      <NextSeo
        config={{
          openGraph: {
            title: pageTitle,
            locale: language,
            url,
            images: [{ url: property?.socialFallbackImage?.url }],
          },
        }}
      />
    </>
  );
};

HomePage.getInitialProps = async ({
  store,
  property,
  language,
  url,
  moduleStatuses,
}: NextPageContext) => {
  const { id: siteId } = property;

  await store.dispatch(entityActions.fetchHomepage(property.id, language));

  const client = new QueryClient();
  const dataRequests: Array<Promise<void>> = [];

  if (moduleStatuses.roomsEnabled) {
    const roomsPageQuery = client.prefetchQuery({
      queryKey: roomsKeys.roomSettings({ siteId, language }),
      queryFn: () => getRoomPageSettings({ siteId, language }),
      cacheTime: Infinity,
      staleTime: Infinity,
    });

    const staticRoomsQuery = client.prefetchQuery({
      queryKey: roomsKeys.staticRooms({ siteId }),
      queryFn: () => getStaticRooms({ siteId }),
      cacheTime: Infinity,
      staleTime: Infinity,
    });

    dataRequests.push(roomsPageQuery, staticRoomsQuery);
  }

  const { isFeaturedSpaDaysVisible, isFeaturedSpaTreatmentsVisible } = getFeaturedModuleVisibility(
    property.modulesExcludingDisabled
  );

  if (isFeaturedSpaDaysVisible) {
    dataRequests.push(
      getAllSpaPackagesPrefetch(client, property.id, language),
      getSpaDaysPagePrefetch(client, property.id, language)
    );
  }

  if (isFeaturedSpaTreatmentsVisible) {
    dataRequests.push(
      getAvailableSpaTreatmentsPrefetch(client, property.id, language, null, null, 6, true),
      getSpaTreatmentsPagePrefetch(client, property.id, language)
    );
  }

  if (property.isTablesIntegrated) {
    dataRequests.push(store.dispatch(entityActions.fetchTablesPage(property.id, language)));
  }

  await Promise.all(dataRequests);

  return {
    url,
    language,
    property,
    socialFallbackImage: property.socialFallbackImage,
    dehydratedState: dehydrate(client),
  };
};

export default HomePage;
